import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, Select } from '@components/ui';

const statusOptions = [
  { value: 'active', label: 'Activo' },
  { value: 'on_hold', label: 'En Pausa' },
  { value: 'completed', label: 'Completado' },
  { value: 'cancelled', label: 'Cancelado' },
];

export default function ProjectModal({
  isOpen,
  onClose,
  onSubmit,
  project = null,
  clients = [],
  isLoading = false,
  onNewClient,
  onEditClient,
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientId: '',
    status: 'active',
    startDate: '',
    endDate: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setErrors({});
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        clientId: project.clientId || '',
        status: project.status?.toLowerCase() || 'active',
        startDate: project.startDate ? project.startDate.split('T')[0] : '',
        endDate: project.endDate ? project.endDate.split('T')[0] : '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        clientId: '',
        status: 'active',
        startDate: '',
        endDate: '',
      });
    }
  }, [project, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validar campos requeridos
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del proyecto es requerido';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit({
      ...formData,
      clientId: formData.clientId ? parseInt(formData.clientId) : null,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
    });
  };

  const clientOptions = clients.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={project ? 'Editar Proyecto' : 'Nuevo Proyecto'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isLoading}>
            {project ? 'Guardar' : 'Crear Proyecto'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <Input
          label="Nombre del Proyecto"
          name="name"
          value={formData.name}
          onChange={(e) => {
            handleChange(e);
            if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
          }}
          placeholder="Nombre del proyecto"
          required
          error={errors.name}
        />

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Descripción
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe el proyecto..."
            rows={3}
            className="input resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-5">
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label className="block text-sm font-medium text-slate-700">
                Cliente
              </label>
              <div style={{ display: 'flex', gap: '4px' }}>
                {formData.clientId && onEditClient && (
                  <button
                    type="button"
                    onClick={() => onEditClient(clients.find(c => c.id === parseInt(formData.clientId)))}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      background: '#f1f5f9',
                      color: '#475569',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                    title="Editar cliente"
                  >
                    ✏️
                  </button>
                )}
                {onNewClient && (
                  <button
                    type="button"
                    onClick={onNewClient}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    + Nuevo
                  </button>
                )}
              </div>
            </div>
            <Select
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              options={clientOptions}
              placeholder="Seleccionar cliente"
            />
          </div>

          <Select
            label="Estado"
            name="status"
            value={formData.status}
            onChange={handleChange}
            options={statusOptions}
          />
        </div>

        <div className="grid grid-cols-2 gap-5">
          <Input
            label="Fecha de Inicio"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={handleChange}
          />

          <Input
            label="Fecha de Fin"
            name="endDate"
            type="date"
            value={formData.endDate}
            onChange={handleChange}
            min={formData.startDate || undefined}
          />
        </div>
      </form>
    </Modal>
  );
}

ProjectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  project: PropTypes.object,
  clients: PropTypes.array,
  isLoading: PropTypes.bool,
  onNewClient: PropTypes.func,
  onEditClient: PropTypes.func,
};
